import { Component, OnChanges, Input } from "@angular/core";

import { APIResults } from "src/app/sharedinterfaces";
import { ApiService } from "src/app/api.service";
import { ChartingService } from "src/app/charting.service";
import { DvsUtilities } from "src/app/dvs-utilities";
declare const BrandVars;

@Component({
	selector: "app-track-coefficients",
	templateUrl: "./track-coefficients.component.html",
	styleUrls: ["./track-coefficients.component.scss"]
})
export class TrackCoefficientsComponent implements OnChanges {
	@Input()
	asset: APIResults.FleetAsset;
	@Input()
	fleet: APIResults.Fleet;
	@Input()
	rotor: APIResults.Rotor;
	@Input()
	regimeConfigs: APIResults.RegimeIndex;

	public shaftOrder = 1;
	public adjustments: number[][];
	public operations: APIResults.Operation[];
	public opBefore: APIResults.Operation;
	public opAfter: APIResults.Operation;

	public trackOptions: Highcharts.Options;
	public timelineOptions: Highcharts.Options;

	public coefficientText?: string;
	public errormessage?: string;

	constructor(private api: ApiService, private charting: ChartingService) { }

	async ngOnChanges() {
		// Initialize empty adjustments - note must make sure rows are different arrays
		this.adjustments = Array(this.rotor.balancedata.numadjustmenttypes).fill([]).map(() => Array(this.rotor.balancedata.numblades).fill(0));
		await this.refreshOperations();
	}

	async refreshOperations() {
		this.operations = undefined;
		const operations = await this.api.GetTrackOperations(this.asset.asset_id, this.rotor.rotorid, 800, true);
		this.operations = operations;
		if (this.operations.length > 0) {
			let selectedOp: APIResults.Operation;
			// Currently tries to select first op with rotor turn >= 5 minutes
			if (!BrandVars.fixedassets) {
				selectedOp = this.operations.find(item => item.rotor_turn_time > 300);
			}
			if (!selectedOp) {
				selectedOp = this.operations[0];
			}
			this.opBefore = selectedOp;
		}
		this.opAfter = this.opBefore;
		await this.updateBothPlots();
	}

	async updateBothPlots() {
		await Promise.all([
			this.updatePlot(),
			this.updateTimeline(),
		]);
	}

	async updatePlot() {
		this.errormessage = undefined;
		if (this.opBefore) {
			// Clear the plots
			this.trackOptions = undefined;
			try {
				const [beforeRegimes, afterRegimes] = await Promise.all([
					this.api.GetRegimeCounts(this.asset.asset_id, this.rotor.rotorid, this.opBefore.operation_id),
					this.api.GetRegimeCounts(this.asset.asset_id, this.rotor.rotorid, this.opAfter.operation_id),
				]);
				const badops = [];
				if (beforeRegimes.track.length === 0) {
					badops.push("Before");
				}
				if (afterRegimes.track.length === 0) {
					badops.push("After");
				}
				if (badops.length > 0) {
					this.errormessage = "No data for " + badops.join(", ") + " operation";
					return;
				}
				const allOn = Array(this.rotor.balancedata.numadjustmenttypes).fill(true);
				const [beforeResults, afterResults] = await Promise.all([
					this.api.GetRTBData(this.asset.asset_id, this.rotor.rotorid, this.opBefore.operation_id, [], this.shaftOrder, allOn, false, true),
					this.api.GetRTBData(this.asset.asset_id, this.rotor.rotorid, this.opAfter.operation_id, [], this.shaftOrder, allOn, false, true),
				]);
				const series: Highcharts.SeriesScatterOptions[] = [];
				const bladespan = 360 / this.rotor.blade.length;
				const allRegimes = Object.keys({ ...beforeResults.track, ...afterResults.track });
				for (const regimeKey of allRegimes) {
					const regime = this.regimeConfigs[Number(regimeKey)];
					const points: any[] = [];
					if (beforeResults.track[regimeKey]) {
						points.push(...beforeResults.track[regimeKey].track.map((value, index) => {
							return {
								x: index + beforeResults.track[regimeKey].leadlag[index] / bladespan,
								leadlag: beforeResults.track[regimeKey].leadlag[index],
								y: value, name: "Before Averages",
								marker: { fillColor: "#027A85", radius: 5 },
							};
						}));
					}
					if (afterResults.track[regimeKey]) {
						points.push(...afterResults.track[regimeKey].track.map((value, index) => {
							return {
								x: index + afterResults.track[regimeKey].leadlag[index] / bladespan,
								leadlag: afterResults.track[regimeKey].leadlag[index],
								y: value, name: "After Averages",
								marker: { fillColor: "#DBCD00", radius: 5 },
							};
						}));
					}
					series.push({ name: regime.name, type: "scatter", data: points });
				}

				this.trackOptions = this.charting.RTBTrackCoeff(series, this.ReorderBlades(this.rotor.blade.map(item => item.name)));
			} catch (err) {
				console.log(err);
				this.errormessage = "Couldn't load track data";
				const remotemessage = DvsUtilities.GetRemoteErrorMessage(err);
				if (remotemessage) {
					this.errormessage += "\n\n" + remotemessage;
				}
			}
		}
	}

	async updateTimeline() {
		this.timelineOptions = undefined;
		const [adjustments, history] = await Promise.all([
			this.api.GetAdjustmentHistory(this.asset.asset_id, this.rotor.rotorid),
			this.api.GetTrackHistory(this.asset.asset_id, this.rotor.rotorid, []),
		]);
		const timelineSeries: Highcharts.SeriesLineOptions[] = [];
		for (const regimeData of history) {
			let regimeLookup = this.regimeConfigs[regimeData.regime];
			if (!regimeLookup) regimeLookup = { name: "Unknown regime " + regimeData.regime, default: false };
			const timelinePoints: any[] = [];
			for (const row of regimeData.rows) {
				timelinePoints.push({ x: row[0] * 1000, y: row[1] });
			}
			timelineSeries.push({
				name: regimeLookup.name,
				type: "line",
				data: timelinePoints,
			});
		}
		this.timelineOptions = this.charting.RTBHistoryTimelineDefaults(timelineSeries, "Split (mm)", this.fleet ? this.fleet.time_offset_hours : 0, adjustments, undefined, this);
	}

	async CalculateCoefficients() {
		this.coefficientText = undefined;
		const result = await this.api.GetTrackCoefficients(this.asset.asset_id, this.rotor.rotorid, this.opBefore.operation_id, this.opAfter.operation_id, this.adjustments);
		this.coefficientText = result.data.join("\n");
	}

	public ReorderBlades(filtered: any[]) {
		if (this.rotor.balancedata.clockwise && filtered.length > 1) {
			// Reorder blades if clockwise, see email "sorry, learned something new" 9/24/2025
			const reordered = filtered.slice().reverse();
			reordered.unshift(reordered.pop());
			return reordered;
		}
		return filtered;
	}
}
